/**
 * Pure rules governing the "upload document → run analysis" flow.
 *
 * Used by every UI that accepts uploads (dashboard, landing drop-zone, add-in).
 * No Angular, no HttpClient, no I/O — these are plain functions over plain
 * values so they can be unit-tested and shared across shells.
 */

/** Filename extensions the analysis pipeline accepts for upload. */
export const ALLOWED_UPLOAD_EXTENSIONS = ['.pdf', '.docx', '.doc'] as const;

/** Hard limit enforced client-side before any upload request is made. */
export const MAX_UPLOAD_SIZE_BYTES = 10 * 1024 * 1024;

/**
 * Minimum number of non-whitespace characters the `extract-text` response
 * must contain for a file to be considered machine-readable. Scanned,
 * encrypted, or image-only PDFs typically come back well under this
 * threshold.
 */
export const MIN_EXTRACTED_TEXT_LENGTH = 50;

/** Shape of validation failures returned by the rules in this module. */
export type UploadValidationError =
  | 'unsupported-extension'
  | 'file-too-large'
  | 'insufficient-credits'
  | 'unreadable-file';

/**
 * True when the file's extension is in the allowlist. Matches case-insensitively.
 */
export function isAllowedUploadExtension(filename: string): boolean {
  const lower = filename.toLowerCase();
  return ALLOWED_UPLOAD_EXTENSIONS.some((ext) => lower.endsWith(ext));
}

/** True when the file size is within the 10 MB cap. */
export function isAllowedUploadSize(sizeBytes: number): boolean {
  return sizeBytes > 0 && sizeBytes <= MAX_UPLOAD_SIZE_BYTES;
}

/**
 * True when the user has ANY positive spendable balance. Unit-agnostic `> 0`
 * check — callers now pass the ACTIVE plan's spendable balance in integer
 * credits (`ActiveSubscriptionUsage.creditsAvailable`, C7). There is no
 * upfront reserve or minimum floor: an analysis may start on any spendable
 * balance and is blocked only once the balance is exhausted, mirroring the
 * backend gate in registry-api `start-analysis` (spendable ≤ 0 → 429).
 * Advisory only — the backend re-checks and may reject.
 */
export function canStartAnalysis(availableTokens: number | undefined | null): boolean {
  return (
    typeof availableTokens === 'number' && Number.isFinite(availableTokens) && availableTokens > 0
  );
}

/** Minimal structural shape of a token-ledger entry (avoids a data-access dep). */
export interface LedgerEntryLike {
  report_id?: string | null;
  type?: string | null;
  amount_tokens?: number | null;
  /** Integer credits (ceil), C7. */
  amount_credits?: number | null;
}

/**
 * Credits arrive from the API as integers (master plan C1: the backend is the
 * single credit authority; the frontend never converts tokens). Formats one
 * for display with en-US thousands separators. Negatives / non-finite clamp
 * to 0; stray fractional input is truncated, never rounded up (a balance must
 * never display more than is spendable).
 */
export function formatCredits(credits: number | null | undefined): string {
  const c =
    typeof credits === 'number' && Number.isFinite(credits) ? Math.max(Math.trunc(credits), 0) : 0;
  return c.toLocaleString('en-US');
}

/** Fraction of the monthly allowance at/under which the balance reads as LOW. */
export const LOW_BALANCE_ALLOWANCE_FRACTION = 0.1;

/**
 * LOW floor (integer credits) when there is no monthly allowance to take a
 * fraction of — wallet mode: the Free one-time grant and the personal wallet.
 * ≈ one mid-band full analysis (FULL_ANALYSIS_CREDITS_RANGE).
 */
export const LOW_BALANCE_WALLET_CREDITS = 20;

/**
 * True when the spendable credit balance warrants the low-balance warning:
 * ≤ ceil(allowance × LOW_BALANCE_ALLOWANCE_FRACTION) on monthly plans (≈ one
 * typical full analysis of headroom — same intent as the retired
 * LOW_BALANCE_TOKENS heuristic), or ≤ LOW_BALANCE_WALLET_CREDITS when there
 * is no monthly allowance. Missing balances count as low (0).
 */
export function isLowCreditBalance(
  available: number | null | undefined,
  monthlyAllowance: number | null | undefined,
): boolean {
  const allowance = num(monthlyAllowance);
  const threshold =
    allowance > 0
      ? Math.ceil(allowance * LOW_BALANCE_ALLOWANCE_FRACTION)
      : LOW_BALANCE_WALLET_CREDITS;
  return num(available) <= threshold;
}

/** Monthly credit allowances per plan (2026-06-29 B2C pricing mockup; Free is one-time). */
export const PLAN_CREDIT_ALLOWANCES = { free: 100, researcher: 300, researcherPro: 700 } as const;

/** Indicative credit cost of a full manuscript analysis (mockup "How credits work"). */
export const FULL_ANALYSIS_CREDITS_RANGE = [10, 80] as const;

/** Indicative credit cost of a single-section re-analysis. */
export const SECTION_REANALYSIS_CREDITS_RANGE = [1, 45] as const;

/** Credit cost of one Reference Search query on paid plans (master plan C6). */
export const REFERENCE_QUERY_CREDITS = 2;

/**
 * Sum the settled (`debit`) credits consumed per report from ledger entries —
 * the integer-credit twin of the retired token-denominated tokensByReport,
 * reading the C7
 * `amount_credits` field. Drives the per-report cost on the dashboard.
 */
export function creditsByReport(
  entries: LedgerEntryLike[] | null | undefined,
): Record<string, number> {
  const out: Record<string, number> = {};
  for (const e of entries ?? []) {
    if (!e || (e.type ?? '').toLowerCase() !== 'debit') continue;
    const id = e.report_id;
    const amount =
      typeof e.amount_credits === 'number' && Number.isFinite(e.amount_credits)
        ? e.amount_credits
        : 0;
    if (!id || amount <= 0) continue;
    out[id] = (out[id] ?? 0) + amount;
  }
  return out;
}

/**
 * True when the text returned by `extract-text` is long enough to proceed
 * with analysis. Trimmed length only — whitespace-only extractions count
 * as unreadable.
 */
export function hasExtractableText(text: string | null | undefined): boolean {
  return typeof text === 'string' && text.trim().length >= MIN_EXTRACTED_TEXT_LENGTH;
}

/**
 * Runs the synchronous-only validations against a file selection and
 * returns the first failure, or `null` when the file passes all local
 * checks. Callers still need to verify `extract-text` output separately.
 * `availableCredits` is the ACTIVE plan's spendable balance in integer
 * credits (C7 — the backend floor already nets out any legacy holds).
 */
export function validateUploadCandidate(
  file: Pick<File, 'name' | 'size'>,
  availableCredits: number | undefined | null,
): UploadValidationError | null {
  if (!isAllowedUploadExtension(file.name)) return 'unsupported-extension';
  if (!isAllowedUploadSize(file.size)) return 'file-too-large';
  if (!canStartAnalysis(availableCredits)) return 'insufficient-credits';
  return null;
}

/** Tri-state classification of a report's processing/analysis status. */
export type AnalysisStatusClass = 'in-progress' | 'succeeded' | 'failed';

/**
 * Canonical classification of a report's processing/analysis status.
 * Inverted by design: only the explicit in-progress and success sets are
 * recognized; every other terminal string (FAILED, ERROR, FAILURE,
 * CANCELLED, …) classifies as `failed`. Empty/unknown counts as in-progress
 * so pollers keep ticking (backstopped by their timeout) rather than
 * releasing the lock early. Fail-open: an unrecognized status can never wedge
 * the lock.
 */
export function classifyAnalysisStatus(status: string | null | undefined): AnalysisStatusClass {
  const s = (status ?? '').toString().trim().toUpperCase();
  if (s === '' || s === 'PENDING' || s === 'PROCESSING') return 'in-progress';
  if (s === 'SUCCEEDED' || s === 'SUCCESS') return 'succeeded';
  return 'failed';
}

/**
 * Terminal-state check for the report's `processing_status`. True once the
 * pipeline is no longer running (succeeded or failed under any status string).
 * Empty/unknown is still in-flight, so this returns false for it.
 */
export function isProcessingComplete(status: string | null | undefined): boolean {
  const s = (status ?? '').toString().trim();
  if (s === '') return false;
  return classifyAnalysisStatus(s) !== 'in-progress';
}

/**
 * True when the report's processing pipeline is still running. `PENDING` and
 * `PROCESSING` are treated as equivalent. Empty/unknown returns false to
 * preserve the previous behavior (an absent status is not asserted in-flight).
 */
export function isProcessingInFlight(status: string | null | undefined): boolean {
  const s = (status ?? '').toString().trim();
  if (s === '') return false;
  return classifyAnalysisStatus(s) === 'in-progress';
}

/** Numeric guard — treats non-finite/missing values as 0. */
function num(v: number | undefined | null): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : 0;
}

/**
 * Fraction of the monthly allowance already consumed, clamped to 0..1, for the
 * progress bar. Returns 0 when there is no allowance (avoids divide-by-zero).
 */
export function consumedFraction(
  allowance: number | undefined | null,
  consumed: number | undefined | null,
): number {
  const a = num(allowance);
  if (a <= 0) return 0;
  return Math.min(Math.max(num(consumed) / a, 0), 1);
}

/** True when the user has a positive monthly allowance (i.e. an active plan). */
export function hasMonthlyAllowance(allowance: number | undefined | null): boolean {
  return num(allowance) > 0;
}

/** Why a (re)analysis is blocked, driving the plan-aware message. */
export type TokenGateReason = 'no-plan' | 'free-used' | 'free' | 'paid-reset' | 'paid-topup';

/** Outcome of the pre-flight token gate for a (re)analysis. */
export interface TokenGateState {
  /** True when the spendable balance covers the start threshold. */
  allowed: boolean;
  /** Set only when `allowed` is false. */
  reason?: TokenGateReason;
  /** ISO reset date to surface; set only for `paid-reset`. */
  resetDate?: string | null;
}

/** Plan-aware copy for a blocked gate. Never surfaces the word "tokens". */
export interface TokenGateCopy {
  title: string;
  body: string;
  /** Primary CTA label, or null when the message is informational only. */
  ctaLabel: string | null;
}

/**
 * Pure pre-flight decision for starting a (re)analysis. A user with NO plan
 * (never subscribed the Free plan nor any paid one) is blocked outright —
 * subscribing a plan on /pricing is the only way in. Otherwise blocks only
 * when the spendable balance is exhausted (≤ 0), mirroring the backend gate —
 * there is no minimum floor and no upfront reserve. `spendable` is the ACTIVE
 * plan's spendable balance in integer credits
 * (`ActiveSubscriptionUsage.creditsAvailable`, C7 — the backend floor already
 * nets out any legacy holds; callers no longer subtract reservations). When
 * blocked,
 * classifies the reason so the UI can show the right message:
 *  - `no-plan`    → no plan at all, must subscribe one (Free or paid);
 *  - `free-used`  → the one-time Free benefit went to a previous (deleted)
 *                   account with this email (backend `free_plan_consumed`) —
 *                   only a paid plan restores access;
 *  - `free`       → free plan, offer an upgrade;
 *  - `paid-reset` → paid plan with a known monthly reset date (echoed back);
 *  - `paid-topup` → paid plan with no reset (wallet / pay-as-you-go top-ups).
 * `freePlanConsumed` comes ONLY from the backend profile field — a zero
 * balance never implies it. It is checked AFTER the spendable check so
 * purchased credits (PAYG top-ups, paid pools) always work.
 * `isFreePlan`/`hasNoPlan`/`freePlanConsumed` should be false while the
 * profile is still loading so a paid user is never mis-classified.
 */
export function resolveTokenGate(input: {
  spendable: number | null | undefined;
  isFreePlan: boolean;
  resetDate: string | null;
  hasNoPlan?: boolean;
  freePlanConsumed?: boolean;
}): TokenGateState {
  const { spendable, isFreePlan, resetDate, hasNoPlan, freePlanConsumed } = input;
  if (hasNoPlan) return { allowed: false, reason: 'no-plan' };
  if (canStartAnalysis(spendable)) return { allowed: true };
  if (freePlanConsumed) return { allowed: false, reason: 'free-used' };
  if (isFreePlan) return { allowed: false, reason: 'free' };
  if (resetDate) return { allowed: false, reason: 'paid-reset', resetDate };
  return { allowed: false, reason: 'paid-topup' };
}

/** Maps a {@link TokenGateReason} to display copy (single source for dialog + banner). */
export function tokenGateCopy(reason: TokenGateReason): TokenGateCopy {
  switch (reason) {
    case 'no-plan':
      return {
        title: 'Subscribe a plan to continue',
        body: 'You need an active plan to use this feature. Pick one on the pricing page. The Free plan is available at no cost.',
        ctaLabel: 'See plans',
      };
    case 'free-used':
      return {
        title: 'Report analysis is not available on this account',
        body: 'The Free plan can only be used once, and this account has already received that benefit. Upgrade to a paid plan to analyse more reports.',
        ctaLabel: 'Explore paid plans',
      };
    case 'free':
      return {
        title: "You're out of credits",
        body: "You don't have enough credits to run this analysis.",
        ctaLabel: 'Upgrade plan',
      };
    case 'paid-reset':
      return {
        title: "You're out of credits",
        body: "You've used this month's credits. Unused credits do not roll over.",
        ctaLabel: null,
      };
    case 'paid-topup':
      return {
        title: "You're out of credits",
        body: "You've used your available credits. Top-up credits expire at the end of your billing cycle.",
        ctaLabel: 'Top up',
      };
  }
}
